import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  /** Visueller Error-State (Border + Text in it-red) */
  hasError?: boolean;
  /** Optional Icon links im Input (z.B. Mail-Icon) */
  iconLeft?: React.ReactNode;
  /** Optional Icon/Action rechts (z.B. Show-Password-Toggle) */
  iconRight?: React.ReactNode;
};

/**
 * Sizilianer Text-Input.
 *
 * Default-Look: Paper-2-Hintergrund, gold-Border on focus.
 * `hasError` setzt Border + Text auf it-red.
 *
 * @example
 *   <Input type="email" placeholder="ihre@email.de" autoComplete="email" />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { hasError, iconLeft, iconRight, className, type = 'text', ...props },
  ref,
) {
  const wrappedInIcons = !!iconLeft || !!iconRight;

  const inputElement = (
    <input
      ref={ref}
      type={type}
      className={cn(
        'w-full rounded-md',
        'bg-paper-2 text-ink-2 placeholder:text-ink-mute',
        'font-sans text-base',
        'h-11 px-3.5',
        // Border / Focus
        hasError
          ? 'border border-it-red focus:outline-2 focus:outline-it-red'
          : 'border border-rule focus:border-gold focus:outline-2 focus:outline-gold/40 focus:outline-offset-1',
        // Disabled
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'transition-colors',
        // Icon-Padding
        iconLeft ? 'pl-10' : null,
        iconRight ? 'pr-10' : null,
        className,
      )}
      {...props}
    />
  );

  if (!wrappedInIcons) return inputElement;

  return (
    <div className="relative">
      {iconLeft && (
        <span
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute"
          aria-hidden="true"
        >
          {iconLeft}
        </span>
      )}
      {inputElement}
      {iconRight && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-mute">
          {iconRight}
        </span>
      )}
    </div>
  );
});
